import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Play, X, Check, Clock, AlertCircle, CheckCircle, RefreshCwIcon, Code, Database, XCircle, MessageSquare, ChevronDown, ChevronUp, StopCircle, ExternalLink } from 'lucide-react';
import { useNotification } from '../contexts/NotificationContext';
import { dataTransformationService, uploadService } from '../services/api';
import Editor from '@monaco-editor/react';

// Define transformation phases
const PHASES = [
  { id: 1, name: 'Validation', key: 'validating', icon: CheckCircle },
  { id: 2, name: 'Code Generation', key: 'generating', icon: Code },
  { id: 3, name: 'Review & Approval', key: 'awaiting_approval', icon: Clock },
  { id: 4, name: 'Completed', key: 'completed', icon: CheckCircle }
];

// Status to phase mapping
const STATUS_PHASE_MAP = {
  'pending': null,
  'validating': 'validating',
  'generating': 'generating',
  'awaiting_approval': 'awaiting_approval',
  'executing': 'executing',
  'completed': 'completed',
  'failed': null,  // Failed status doesn't map to a specific phase
  'cancelled': null  // Cancelled status doesn't map to a specific phase
};

// Checkpoint to completed phases mapping
const CHECKPOINT_PHASE_MAP = {
  'none': [],
  'validated': ['validating'],
  'approved': ['validating', 'generating', 'awaiting_approval'],
  'completed': ['validating', 'generating', 'awaiting_approval', 'completed']
};

const DataTransformationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState('');
  const [processing, setProcessing] = useState(false);
  const [datasetDetails, setDatasetDetails] = useState({});
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsInitialized, setRunsInitialized] = useState(false);

  // Polling intervals in milliseconds
  const POLLING_INTERVAL_EXECUTING = 2000;
  const POLLING_INTERVAL_DEFAULT = 5000;

  useEffect(() => {
    if (id) {
      fetchJobDetails();
    }
  }, [id]);

  // Memoize fetchJobDetails to use in useEffect
  const fetchJobDetailsCallback = useCallback(() => fetchJobDetails(true), [id]);

  // Separate polling effect that depends on job status
  useEffect(() => {
    if (!job || !id) return;

    const isExecuting = job.status === 'executing';
    const isInProgress = ['pending', 'validating', 'generating', 'awaiting_approval', 'executing'].includes(job.status);
    
    // Only poll if job is in progress or executing
    if (isInProgress || isExecuting) {
      const interval = setInterval(() => {
        fetchJobDetailsCallback(); // Silent update
      }, isExecuting ? POLLING_INTERVAL_EXECUTING : POLLING_INTERVAL_DEFAULT);
      
      return () => clearInterval(interval);
    }
  }, [id, job?.status, fetchJobDetailsCallback, POLLING_INTERVAL_EXECUTING, POLLING_INTERVAL_DEFAULT]);

  // Helper function to format dataset name
  const formatDatasetName = (name) => {
    if (!name) return '';
    
    // If name is just a UUID, return a generic name
    if (/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(name)) {
      return 'Dataset';
    }
    
    // Remove file extension if present
    const nameWithoutExt = name.replace(/\.[^/.]+$/, '');
    
    // Remove UUID suffix if present
    const cleanName = nameWithoutExt.replace(/_[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/, '');
    
    // Convert snake_case or kebab-case to Title Case
    return cleanName
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const fetchJobDetails = async (silent = false) => {
    try {
      const response = await dataTransformationService.getJobDetails(id);
      
      if (response) {
        // Only update if something changed
        if (silent) {
          setJob(prevJob => {
            // Check if status or checkpoint changed
            if (!prevJob || 
                prevJob.status !== response.status || 
                prevJob.checkpoint !== response.checkpoint ||
                prevJob.updated_at !== response.updated_at) {
              return response;
            }
            return prevJob;
          });
        } else {
          setJob(response);
        }
        
        // Fetch runs if job is approved or completed
        if (response.status === 'completed' && (response.checkpoint === 'approved' || response.checkpoint === 'completed')) {
          fetchRuns(silent);
        }
        
        // Only fetch dataset details on initial load
        if (!silent && response.source_datasets?.length > 0) {
          try {
            const datasets = await uploadService.getDatasets();
            const details = {};
            
            // First try to find in grouped datasets
            response.source_datasets.forEach(source => {
              const datasetId = source.dataset_id;
              // Find the dataset in the grouped datasets
              Object.entries(datasets.datasets || {}).forEach(([name, versions]) => {
                const version = versions.find(v => v.dataset_id === datasetId);
                if (version) {
                  details[datasetId] = {
                    ...version,
                    name: name,
                    displayName: formatDatasetName(name)
                  };
                }
              });
            });
            
            // For any missing datasets, fetch them individually
            for (const source of response.source_datasets) {
              if (!details[source.dataset_id]) {
                try {
                  const dataset = await uploadService.getUpload(source.dataset_id);
                  if (dataset) {
                    details[source.dataset_id] = {
                      ...dataset,
                      name: dataset.name || dataset.filename,
                      displayName: formatDatasetName(dataset.name || dataset.filename)
                    };
                  }
                } catch (err) {
                  console.error(`Error fetching individual dataset ${source.dataset_id}:`, err);
                }
              }
            }
            
            setDatasetDetails(details);
          } catch (error) {
            console.error('Error fetching dataset details:', error);
            // Continue without dataset details
          }
        }
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      if (!silent) {
        notify.error('Failed to load job details');
        navigate('/data-transformation');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchRuns = async (silent = false) => {
    try {
      // Only show loading on first fetch
      if (!runsInitialized && !silent) {
        setRunsLoading(true);
      }
      
      const response = await dataTransformationService.getJobRuns(id);
      
      if (silent) {
        setRuns(prevRuns => {
          // Only update if runs have changed
          const newRuns = response.runs || [];
          // Compare length and last run's updated_at for efficiency
          if (prevRuns.length !== newRuns.length || 
              (newRuns.length > 0 && prevRuns.length > 0 && 
               newRuns[newRuns.length - 1].updated_at !== prevRuns[prevRuns.length - 1].updated_at)) {
            return newRuns;
          }
          return prevRuns;
        });
      } else {
        setRuns(response.runs || []);
      }
      
      if (!runsInitialized) {
        setRunsInitialized(true);
        setRunsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching runs:', error);
      // Don't show error notification - runs might not exist yet
      if (!runsInitialized && !silent) {
        setRunsLoading(false);
        setRunsInitialized(true);
      }
    }
  };

  const handleApprove = async () => {
    try {
      setProcessing(true);
      await dataTransformationService.approveCode(id, null); // No code editing allowed
      notify.success('Code approved successfully');
      await fetchJobDetails();
    } catch (error) {
      console.error('Error approving code:', error);
      notify.error('Failed to approve code');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    try {
      setProcessing(true);
      await dataTransformationService.rejectCode(id, feedback);
      notify.success('Feedback submitted successfully');
      setShowRejectModal(false);
      setFeedback('');
      await fetchJobDetails();
    } catch (error) {
      console.error('Error rejecting code:', error);
      notify.error('Failed to reject code');
    } finally {
      setProcessing(false);
    }
  };

  const handleExecute = async () => {
    try {
      setProcessing(true);
      await dataTransformationService.executeJob(id);
      notify.success('Transformation execution started');
      await fetchJobDetails();
    } catch (error) {
      console.error('Error executing transformation:', error);
      notify.error('Failed to execute transformation');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    try {
      setProcessing(true);
      await dataTransformationService.cancelJob(id);
      notify.success('Job cancelled successfully');
      await fetchJobDetails();
    } catch (error) {
      console.error('Error cancelling job:', error);
      notify.error('Failed to cancel job');
    } finally {
      setProcessing(false);
    }
  };

  const handleRetry = async () => {
    try {
      setProcessing(true);
      // Determine retry type based on current status
      let retryType = 'from_checkpoint';
      const checkpoint = job?.checkpoint || 'none';
      
      // Retry from the failed phase
      if (checkpoint === 'none') {
        retryType = 'from_start';
      }
      
      await dataTransformationService.retryJob(id, { retry_type: retryType });
      notify.success('Job retry started successfully');
      await fetchJobDetails();
    } catch (error) {
      console.error('Error retrying job:', error);
      notify.error('Failed to retry job');
    } finally {
      setProcessing(false);
    }
  };
  
  const getPhaseStatus = (phase) => {
    const currentStatus = job?.status?.toLowerCase();
    const checkpoint = job?.checkpoint || 'none';
    
    // Special handling for approved jobs (completed with approved checkpoint)
    if (currentStatus === 'completed' && checkpoint === 'approved') {
      // All phases are completed when job is approved
      return 'completed';
    }
    
    // Special handling for fully executed jobs (checkpoint completed)
    if (checkpoint === 'completed') {
      // All phases are completed when job is fully executed
      return 'completed';
    }
    
    // Get the current phase based on status
    const currentPhase = STATUS_PHASE_MAP[currentStatus];
    
    // Get phase order
    const phaseOrder = ['validating', 'generating', 'awaiting_approval', 'completed'];
    const currentPhaseIndex = phaseOrder.indexOf(currentPhase);
    const phaseIndex = phaseOrder.indexOf(phase.key);
    
    // If job failed, determine which phase failed based on checkpoint
    if (currentStatus === 'failed' || currentStatus === 'cancelled') {
      // Map checkpoint to the phase that was running when it failed
      const failedPhaseMap = {
        'none': 'validating',
        'validated': 'generating',
        'approved': 'executing',
        'completed': 'completed'
      };
      const failedPhase = failedPhaseMap[checkpoint] || 'validating';
      
      if (phase.key === failedPhase) {
        return 'failed';
      } else if (phaseOrder.indexOf(phase.key) < phaseOrder.indexOf(failedPhase)) {
        return 'completed';
      } else {
        return 'pending';
      }
    }
    
    // Phase is complete only if we've moved past it
    if (phaseIndex < currentPhaseIndex && currentPhaseIndex !== -1) {
      return 'completed';
    }
    
    // Current phase is in progress
    if (currentPhase === phase.key) {
      return 'in_progress';
    }
    
    // Phase hasn't been reached yet
    return 'pending';
  };
  
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
      case 'cancelled':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'executing':
      case 'validating':
      case 'generating':
        return <RefreshCwIcon className="animate-spin h-5 w-5 text-blue-500" />;
      case 'awaiting_approval':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'failed':
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'executing':
      case 'validating':
      case 'generating':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'awaiting_approval':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin h-6 w-6 border-2 border-slate-300 dark:border-slate-600 border-t-slate-600 dark:border-t-slate-300 rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600 dark:text-slate-400">Loading transformation details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-gray-600 dark:text-gray-400">Job not found</div>
        </div>
      </div>
    );
  }
  
  const completedPhases = PHASES.filter(phase => getPhaseStatus(phase) === 'completed').length;
  const progressPercent = (completedPhases / PHASES.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/data-transformation')}
          className="mb-6 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Transformations
        </button>

        {/* Header Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {job.transformation?.name || 'Transformation Job'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {job.transformation?.description || 'No description'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(job.status)}
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(job.status)}`}>
                {job.status?.toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Job Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Job ID</div>
              <div className="text-sm font-mono text-gray-900 dark:text-white break-all">
                {job.job_id}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Created</div>
              <div className="text-sm text-gray-900 dark:text-white">
                {new Date(job.created_at).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Last Updated</div>
              <div className="text-sm text-gray-900 dark:text-white">
                {new Date(job.updated_at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Source Datasets Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Source Datasets</h2>
          </div>
          <div className="space-y-3">
            {job.source_datasets?.map((source, index) => {
              const details = datasetDetails[source.dataset_id];
              
              // Get the dataset name with multiple fallbacks
              const datasetName = details?.displayName || 
                                  formatDatasetName(details?.name) ||
                                  formatDatasetName(details?.filename) ||
                                  (source.dataset_name && source.dataset_name !== source.dataset_id ? formatDatasetName(source.dataset_name) : null) ||
                                  (source.dataset_name ? formatDatasetName(source.dataset_name) : null) ||
                                  'Dataset';
              
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {datasetName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {details ? (
                        <>
                          <span className="break-all">ID: {source.dataset_id}</span>
                          {details.version_number && ` • Version ${details.version_number}`}
                          {details.is_latest && (
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded text-xs">
                              Latest
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="break-all">ID: {source.dataset_id}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {details?.created_at && new Date(details.created_at).toLocaleDateString()}
                    </div>
                    <button
                      onClick={() => navigate(`/datasets/${source.dataset_id}`)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-all"
                      title="View dataset details"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Transformation Pipeline Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                Transformation Pipeline
              </h3>
              <div className="flex items-baseline gap-6 mt-2">
                <span className="font-mono text-2xl font-light text-gray-900 dark:text-white">
                  {completedPhases}<span className="text-gray-400 dark:text-gray-500">/{PHASES.length}</span>
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {progressPercent.toFixed(0)}% complete
                </span>
              </div>
            </div>
          </div>

          {/* Progress Visualization */}
          <div className="space-y-3">
            {PHASES.map((phase) => {
              const phaseStatus = getPhaseStatus(phase);
              const Icon = phase.icon;

              return (
                <div key={phase.id} className="flex items-center gap-4">
                  {/* Phase Number */}
                  <div className="w-8 text-right">
                    <span className={`
                      font-mono text-xs
                      ${phaseStatus === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                        phaseStatus === 'failed' ? 'text-rose-600 dark:text-rose-400' :
                        phaseStatus === 'in_progress' ? 'text-blue-600 dark:text-blue-400' :
                        'text-gray-400 dark:text-gray-500'}
                    `}>
                      {phase.id}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className={`
                    p-2 rounded-lg
                    ${phaseStatus === 'completed' ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400' :
                      phaseStatus === 'failed' ? 'bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400' :
                      phaseStatus === 'in_progress' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}
                  `}>
                    <Icon className="w-4 h-4" />
                  </div>

                  {/* Phase Name */}
                  <div className="flex-1 min-w-0">
                    <div className={`
                      text-sm font-medium
                      ${phaseStatus === 'completed' ? 'text-gray-900 dark:text-white' :
                        phaseStatus === 'failed' ? 'text-rose-900 dark:text-rose-200' :
                        phaseStatus === 'in_progress' ? 'text-gray-900 dark:text-white' :
                        'text-gray-500 dark:text-gray-400'}
                    `}>
                      {phase.name}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="flex-1 min-w-0">
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`
                          h-full transition-all duration-700 ease-out
                          ${phaseStatus === 'completed' ? 'bg-emerald-500 dark:bg-emerald-400 w-full' :
                            phaseStatus === 'failed' ? 'bg-rose-500 dark:bg-rose-400 w-full' :
                            phaseStatus === 'in_progress' ? 'bg-blue-500 dark:bg-blue-400 w-1/2' :
                            'w-0'}
                        `}
                        style={phaseStatus === 'in_progress' ? {
                          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                        } : {}}
                      />
                    </div>
                  </div>

                  {/* Status Indicator */}
                  <div className="w-24 text-right">
                    {phaseStatus === 'failed' ? (
                      <button
                        onClick={handleRetry}
                        disabled={processing}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                      >
                        {processing ? 'Retrying...' : 'Retry'}
                      </button>
                    ) : (
                      <span className={`text-xs font-mono
                        ${phaseStatus === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                          phaseStatus === 'in_progress' ? 'text-blue-600 dark:text-blue-400' :
                          'text-gray-400 dark:text-gray-500'}
                      `}>
                        {phaseStatus === 'completed' ? 'Done' :
                         phaseStatus === 'in_progress' ? 'Running' :
                         '—'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Error Message */}
          {job.status === 'failed' && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Status
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                The job encountered an error during processing. Please retry the operation.
              </p>
            </div>
          )}
        </div>

        {/* Code Review Section - Show when awaiting approval, completed (approved), or executing */}
        {(job.status === 'awaiting_approval' || job.status === 'completed' || job.status === 'executing') && job.transformation?.code && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Generated Code
                </h3>
              </div>
            </div>
            
            {job.transformation?.summary && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-900 dark:text-blue-200">
                  {job.transformation.summary}
                </p>
              </div>
            )}
            
            {/* Code Display Section */}
            <div className="mb-4 border border-gray-700 rounded-lg overflow-hidden">
              <Editor
                height="400px"
                defaultLanguage="python"
                theme="vs-dark"
                value={job.transformation.code}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  automaticLayout: true,
                  padding: { top: 16, bottom: 16 }
                }}
              />
            </div>
            
            {/* Action Buttons */}
            {job.status === 'awaiting_approval' && (
              <div className="flex gap-3">
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 font-medium transition-colors"
                >
                  {processing ? (
                    <RefreshCwIcon className="animate-spin h-4 w-4" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Approve Code
                </button>
                
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 font-medium transition-colors"
                >
                  <X className="w-4 h-4" />
                  Request Changes
                </button>
              </div>
            )}
            
            {job.status === 'completed' && (job.checkpoint === 'approved' || job.checkpoint === 'completed') && (
              <button
                onClick={handleExecute}
                disabled={processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 font-medium transition-colors"
              >
                {processing ? (
                  <RefreshCwIcon className="animate-spin h-4 w-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Execute Transformation
              </button>
            )}
            
            {job.status === 'executing' && (
              <button
                onClick={handleCancel}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 disabled:opacity-50 font-medium transition-colors"
              >
                {processing ? (
                  <RefreshCwIcon className="animate-spin h-4 w-4" />
                ) : (
                  <StopCircle className="w-4 h-4" />
                )}
                Cancel Execution
              </button>
            )}
          </div>
        )}

        {/* Feedback History Section - Only show if there's user feedback */}
        {job.transformation?.history?.some(entry => entry.action === 'user_rejected') && (() => {
          const feedbackCount = job.transformation.history.filter(e => e.action === 'user_rejected').length;
          return (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between mb-3 group"
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Feedback History
                </h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {feedbackCount} feedback {feedbackCount === 1 ? 'round' : 'rounds'}
                </span>
              </div>
              {showHistory ? (
                <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
              )}
            </button>
            
            {showHistory && (
              <div className="space-y-4">
                {job.transformation.history.map((entry, index) => {
                  const isRejection = entry.action === 'user_rejected';
                  const isGeneration = entry.action === 'code_generated';
                  
                  return (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isRejection 
                            ? 'bg-red-100 dark:bg-red-900/30' 
                            : 'bg-blue-100 dark:bg-blue-900/30'
                        }`}>
                          {isRejection ? (
                            <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                          ) : (
                            <Code className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="font-medium text-sm text-gray-900 dark:text-white">
                            {isRejection ? 'Changes Requested' : 'Code Generated'}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(entry.timestamp).toLocaleString()}
                          </span>
                          {entry.version && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              v{entry.version}
                            </span>
                          )}
                        </div>
                        
                        {isRejection && entry.feedback && (
                          <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              "{entry.feedback}"
                            </p>
                          </div>
                        )}
                        
                        {isGeneration && entry.summary && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {entry.summary}
                          </p>
                        )}
                        
                        {index < job.transformation.history.length - 1 && (
                          <div className="ml-4 mt-3 mb-3 border-l-2 border-gray-200 dark:border-gray-700 h-4" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          );
        })()}

        {/* Execution Runs Section - Show if job is approved or completed */}
        {job.status === 'completed' && (job.checkpoint === 'approved' || job.checkpoint === 'completed') && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Play className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Execution History
              </h3>
              {runs.length > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({runs.length} run{runs.length !== 1 ? 's' : ''})
                </span>
              )}
            </div>
            
            {runsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCwIcon className="animate-spin h-6 w-6 text-gray-400" />
              </div>
            ) : runs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No execution runs yet. Click "Execute Transformation" to run this job.
              </p>
            ) : (
              <div className="space-y-3">
                {runs.map((run, index) => {
                  const duration = run.completed_at && run.started_at
                    ? ((new Date(run.completed_at) - new Date(run.started_at)) / 1000).toFixed(1) + 's'
                    : run.started_at && !run.completed_at
                    ? 'Running...'
                    : 'Pending';
                  
                  return (
                    <div
                      key={run.run_id}
                      className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Run #{run.run_number}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              • {new Date(run.created_at).toLocaleString()}
                            </span>
                          </div>
                          
                          {/* Status indicator */}
                          {run.status === 'pending' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          )}
                          {run.status === 'running' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              <RefreshCwIcon className="w-3 h-3 mr-1 animate-spin" />
                              Running
                            </span>
                          )}
                          {run.status === 'success' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Success
                            </span>
                          )}
                          {run.status === 'failed' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                              <XCircle className="w-3 h-3 mr-1" />
                              Failed
                            </span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {duration}
                        </div>
                      </div>
                      
                      {/* Output metadata */}
                      {run.output_metadata && run.output_metadata.row_count > 0 && (
                        <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          Output: {run.output_metadata.row_count.toLocaleString()} rows
                        </div>
                      )}
                      
                      {/* Error message */}
                      {run.error_message && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-300">
                          {run.error_message}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center ${showRejectModal ? 'block' : 'hidden'}`}>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Provide Feedback
            </h3>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Describe what changes you'd like to see..."
              rows={4}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <div className="mt-4 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setFeedback('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !feedback.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {processing && <RefreshCwIcon className="animate-spin h-4 w-4" />}
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataTransformationDetails;
